import { selectSellPriceData } from '../../redux/slices/sellPriceSlice';
import { getScheduleLocation } from '../../utils/shipToLocation';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col, Form, Accordion } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { updateJob } from '../../redux/slices/jobScheduleSlice';
import { patchData as patchDataThunk } from '../../redux/slices/apiCallSlice';
import { selectSchedulePumpDetails } from "../../redux/selectors";
import SaveBanner from '../JobDetailPage/Mechanical/SaveBanner';
import EngineerAssignmentModal from './EngineerAssignmentModal';
import useScheduleChangeLog from '../../functions/useScheduleChangeLog';
import TbdNotice from './TbdNotice';
import MoveRequestNotice from './MoveRequestNotice';
import WitnessTestNotice from './WitnessTestNotice';
import LongBuildNotice from './LongBuildNotice';
import ChangeOrderNotice from './ChangeOrderNotice';
import AlertsModal from './AlertsModal';
import QAFeedback from './QAFeedback';
// Import centralized date utilities
import { 
  formatDateForForm, 
  isValidDateFormat, 
  isWeekendDate, 
  isOutsideCurrentYear 
} from '../../utils/dateUtils';
// Import permission utilities
import { 
  getEditableFields,
  isFieldEditable // <-- import the new helper
} from './utils/schedulePermissions';
import { getMoveRequestIcon } from './utils/jobHelpers';

// Helper function for text processing
const separateCamelCase = (text) => {
  if (text === 'HCPumpsDone') return 'HC Pumps Done';
  if (text === 'PumpDone') return 'VT Pumps Done';

  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Separate camel case
    .replace(/(HC|VT|NP|CSM|HDPE)/g, ' $1 ') // Treat specific abbreviations as single words
    .replace(/\s+/g, ' ') // Remove extra spaces
    .trim();
};

const EditJobModal = ({ selectedJob, setSelectedJob, handleCloseModal, handleSaveChanges }) => {
  // Fix: Properly access the apiUrls from the nested structure in the Redux store
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  const dispatch = useDispatch();
  const soldToShipToByProject = useSelector(selectSellPriceData);
  const options = useSelector((state) => state.dropdown.options); // Get dropdown options from Redux  // Get user dropdowns from Redux store instead of fetching them with the hook
  const userDropdowns = useSelector((state) => state.dropdown.userDropdowns);
  
  // Get user roles from Redux store
  const userRolesMapped = useSelector((state) => state.user?.rolesMapped) || {};
  
  // Get pump details from Redux store for notices
  const pumpDetails = useSelector(selectSchedulePumpDetails);
  
  const mechOptions = userDropdowns.mechOptions || [];
  const elecOptions = userDropdowns.elecOptions || [];
  const controlOptions = userDropdowns.controlOptions || []; // Add control options
  const salesOptions = userDropdowns.salesOptions || []; // Add sales options
  const projectManagerOptions = [
    ...(userDropdowns.appsOptions || []),
    ...(userDropdowns.adminOptions || [])
  ]; // Add project manager options
  
  // Get schedule change logging functionality
  const { logScheduleChange } = useScheduleChangeLog();
  
  // SaveBanner state
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // State for engineer assignment modal
  const [showEngineerModal, setShowEngineerModal] = useState(false);
  // State to track if changes were made
  const [changesMade, setChangesMade] = useState(false);
  
  // State to track original job data for change logging
  const [originalJobData, setOriginalJobData] = useState(null);// State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // State for ship date confirmation modal
  const [showShipConfirmationModal, setShowShipConfirmationModal] = useState(false);
  const [pendingShipDate, setPendingShipDate] = useState('');
  // State for job cancellation modal
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  
  // State for alerts modal
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  // State for QA Feedback modal
  const [showQAFeedback, setShowQAFeedback] = useState(false);
  // State for date input modes
  const [formDateModes, setFormDateModes] = useState({});

  // Capture original job data when modal opens for change tracking
  useEffect(() => {
    if (selectedJob && !originalJobData) {
      setOriginalJobData({ ...selectedJob });
    }
  }, [selectedJob, originalJobData]);

  // Calculate the latest engineering due and done dates
  useEffect(() => {
    if (selectedJob) {
      // Create a copy of the job for modifications
      const updatedJob = { ...selectedJob };
      
      // Determine latest due date from the three engineering sections
      const calculateLatestDate = (dateFields) => {
        const validDates = dateFields
          .map(field => selectedJob[field])
          .filter(Boolean)
          .map(dateStr => new Date(dateStr));
        
        if (validDates.length === 0) return null;
        
        // Find the latest date
        const latestDate = new Date(Math.max.apply(null, validDates.map(date => date.getTime())));
        return isNaN(latestDate.getTime()) ? null : latestDate.toISOString().split('T')[0];
      };
      
      // Calculate latest engineering due date
      const latestEngineeringDue = calculateLatestDate(['MechanicalDue', 'ElectricalDue', 'ControlsDue']);
      if (latestEngineeringDue && (!updatedJob.EngineeringDue || updatedJob.EngineeringDue !== latestEngineeringDue)) {
        updatedJob.EngineeringDue = latestEngineeringDue;
      }
      
      // Calculate latest engineering done date
      const latestEngineeringDone = calculateLatestDate(['MechanicalDone', 'ElectricalDone', 'ControlsDone']);
      if (latestEngineeringDone && (!updatedJob.EngineeringDone || updatedJob.EngineeringDone !== latestEngineeringDone)) {
        updatedJob.EngineeringDone = latestEngineeringDone;
      }
        // Only update the state if changes were made
      if (updatedJob.EngineeringDue !== selectedJob.EngineeringDue || 
          updatedJob.EngineeringDone !== selectedJob.EngineeringDone) {
        setSelectedJob(updatedJob);
      }
    }
  }, [selectedJob?.MechanicalDue, selectedJob?.MechanicalDone, 
       selectedJob?.ElectricalDue, selectedJob?.ElectricalDone, 
       selectedJob?.ControlsDue, selectedJob?.ControlsDone, selectedJob, setSelectedJob]);

  const buildPatchPayload = (originalData, newData) => {
    if (!originalData) {
      return { ...newData };
    }

    const patchPayload = { ID: newData.ID };
    const editableFieldsList = getEditableFields(userRolesMapped);
    const fieldsToCheck = editableFieldsList === 'all' ? Object.keys(newData) : editableFieldsList;

    fieldsToCheck.forEach((field) => {
      if (field === 'ID' || field === 'id') {
        return;
      }

      const originalValue = originalData[field] || '';
      const newValue = newData[field] || '';

      if (String(originalValue).trim() !== String(newValue).trim()) {
        patchPayload[field] = newData[field];
      }
    });

    return patchPayload;
  };

  // Helper function to detect and log changes
  const detectAndLogChanges = async (originalData, newData) => {
    const changedFields = [];
    
    // Get the fields that the user can edit using the mapped roles
    const editableFieldsList = getEditableFields(userRolesMapped);
    const fieldsToCheck = editableFieldsList === 'all' ? Object.keys(newData) : editableFieldsList;
    
    // Find all changed fields
    for (const field of fieldsToCheck) {
      const originalValue = originalData[field] || '';
      const newValue = newData[field] || '';
      
      // Convert values to strings for comparison to handle null/undefined/empty cases
      const originalStr = String(originalValue).trim();
      const newStr = String(newValue).trim();
      
      if (originalStr !== newStr) {
        changedFields.push({
          field,
          originalValue: originalValue,
          newValue: newValue
        });
      }
    }
    
    // Log each change sequentially
    for (const change of changedFields) {
      try {
        await logScheduleChange(
          newData.ID,
          newData.JobNumber || '',
          newData.ProjectName || '',
          change.field,
          change.originalValue,
          change.newValue
        );
        console.log(`Logged change for ${change.field}: "${change.originalValue}" -> "${change.newValue}"`);
      } catch (logError) {
        console.warn(`Failed to log change for field ${change.field}:`, logError);
        // Continue with other changes even if one fails
      }
    }
    
    return changedFields.length;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for DollarValue to prevent NaN formatting issues
    if (name === 'DollarValue') {
      // Remove any non-numeric characters except decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      setSelectedJob(prevJob => ({
        ...prevJob,
        [name]: numericValue
      }));
      setChangesMade(true);
      return;
    }
    
    // Handle ActualShipDate confirmation
    if (name === 'ActualShipDate' && value && !selectedJob.ActualShipDate) {
      setPendingShipDate(value);
      setShowShipConfirmationModal(true);
      return;
    }
    
    // Handle JobStatus change to Canceled
    if (name === 'JobStatus' && value === 'Canceled' && selectedJob.JobStatus !== 'Canceled') {
      // First set the job status
      setSelectedJob((prevJob) => ({ ...prevJob, [name]: value }));
      setChangesMade(true);
      // Then show the confirmation modal for clearing dates
      setShowCancelConfirmationModal(true);
      return;
    }
    
    // Default handling for all other fields
    setSelectedJob((prevJob) => ({ ...prevJob, [name]: value }));
    setChangesMade(true);
  };
  const handleSave = async () => {
    try {
      // Make sure we have original data for comparison
      if (!originalJobData) {
        console.error('No original job data available for change tracking');
        return;
      }

      // Ensure ID exists in the request body
      const jobWithID = { ...selectedJob };
      if (!jobWithID.ID && selectedJob.ID) {
        jobWithID.ID = selectedJob.ID;
      }
      
      // Ensure boolean fields that cannot be null have default values
      const fieldsToDefaultFalse = ['SendDocumentsToSales', 'CustomControlsEngineering', 'NewConstruction', 'HasSubmittal'];
      fieldsToDefaultFalse.forEach(field => {
        if (jobWithID[field] === null || jobWithID[field] === undefined) {
          jobWithID[field] = false;
        }
      });
      
      // Skip internal UI state properties
      const filteredData = {};
      Object.keys(jobWithID).forEach(key => {
        // Skip internal state keys used for the UI
        if (!key.includes('_showAsText')) {
          filteredData[key] = jobWithID[key];
        }
      });

      const patchPayload = buildPatchPayload(originalJobData, filteredData);
      const changedFieldCount = Object.keys(patchPayload).filter((key) => key !== 'ID' && key !== 'id').length;

      if (changedFieldCount === 0) {
        setSaveStatus('No changes to save');
        setShowSaveBanner(true);
        setTimeout(() => {
          setShowSaveBanner(false);
        }, 2000);
        return;
      }
      
      // First: Make the main API call and wait for success
      const updateResponseData = await dispatch(
        patchDataThunk({
          url: `${apiUrls.generalProject}/?id=${filteredData.ID}`,
          data: patchPayload,
          baselineData: originalJobData,
        })
      ).unwrap();
      
      // Only proceed with logging if the main API call succeeded
      if (updateResponseData) {
        console.log('Job updated successfully:', updateResponseData);
          // Second: Log changes to the schedule change log (only if main update succeeded)
        try {
          const changeCount = await detectAndLogChanges(originalJobData, filteredData);
          console.log(`Successfully logged ${changeCount} changes`);
        } catch (logError) {
          console.warn('Main update succeeded but some change logging failed:', logError);
          // Don't throw here - the main update worked
        }
        
        // Third: Update Redux store (only if main update succeeded)
        dispatch(updateJob(patchPayload));
        
        // Show success notification
        setSaveStatus("Successfully updated job");
        setShowSaveBanner(true);
        
        // Hide the notification after 3 seconds
        setTimeout(() => {
          setShowSaveBanner(false);
        }, 3000);
        
        if (handleSaveChanges) {
          handleSaveChanges();
        }

        handleCloseModal(); // Close the modal after saving changes
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      
      // Show error notification
      setSaveStatus(`Failed to update job: ${error.message}`);
      setShowSaveBanner(true);
      
      // Hide the notification after 5 seconds
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    }
  };

  const handleDateInputModeToggle = (field, isTextMode) => {
    setFormDateModes((prevModes) => ({
      ...prevModes,
      [field]: isTextMode
    }));
    
    // Mark that changes have been made
    setChangesMade(true);
  };

  const renderDateField = (field, readOnly = false, allowTextToggle = false) => {
    // Date field handling
    const dateValue = selectedJob[field] || '';
    const formattedDate = formatDateForForm(dateValue);
    const isValidDate = isValidDateFormat(dateValue);
    const isTextMode = formDateModes[field] || false;
    
    // Check for weekend date or outside current year
    const isWeekend = isWeekendDate(dateValue);
    const isOutsideYear = isOutsideCurrentYear(dateValue);
    const hasWarning = isWeekend || isOutsideYear;

    // Use isFieldEditable for readOnly/disabled
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);

    return (
      <Form.Group className="mb-3">
        <div className="d-flex align-items-center mb-2">
          <Form.Label className="mb-0">
            {separateCamelCase(field)}
          </Form.Label>
          {allowTextToggle && (
            <Form.Check
              type="checkbox"
              id={`${field}-text-mode`}
              className="ms-3"
              checked={isTextMode}
              onChange={(e) => handleDateInputModeToggle(field, e.target.checked)}
            />
          )}
        </div>
        {isTextMode || !isValidDate ? (
          <Form.Control
            type="text"
            id={field}
            name={field}
            value={dateValue}
            onChange={handleInputChange}
            placeholder="Enter date as text"
            className={hasWarning ? "border-warning" : ""}
            readOnly={!editable}
            disabled={!editable}
          />
        ) : (
          <Form.Control
            type="date"
            id={field}
            name={field}
            value={formattedDate}
            onChange={handleInputChange}
            readOnly={!editable}
            disabled={!editable}
            className={hasWarning ? "border-warning" : ""}
          />
        )}
        
        {/* Display warnings if applicable */}
        {isWeekend && !isTextMode && (
          <div className="text-warning small mt-1">
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            This date falls on a weekend
          </div>
        )}
        {isOutsideYear && !isTextMode && (
          <div className="text-warning small mt-1">
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            This date is outside the current year ({new Date().getFullYear()})
          </div>
        )}
      </Form.Group>
    );
  };

  const renderField = (field, readOnly = false, isDate = false, allowTextToggle = false) => {
    // Use isFieldEditable for all fields
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);
    const isFieldReadOnly = readOnly || !editable;
    
    if (!isDate) {
      // Check if this is the JobLocation field and provide a custom label
      if (field === 'JobLocation') {
        const location = getScheduleLocation(selectedJob, soldToShipToByProject);
        return (
          <Form.Group className="mb-3">
            <Form.Label>Job Location (City, State, Country)</Form.Label>
            <Form.Control
              type="text"
              id={field}
              name={field}
              value={location.fromShipTo ? location.value : selectedJob[field] || ''}
              onChange={handleInputChange}
              readOnly={isFieldReadOnly || location.fromShipTo}
              disabled={isFieldReadOnly || location.fromShipTo}
            />
          </Form.Group>
        );
      }
      
      return (
        <Form.Group className="mb-3">
          <Form.Label>{separateCamelCase(field)}</Form.Label>
          <Form.Control
            type="text"
            id={field}
            name={field}
            value={selectedJob[field] || ''}
            onChange={handleInputChange}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
          />
        </Form.Group>
      );
    }
    return renderDateField(field, isFieldReadOnly, allowTextToggle);
  };

  const renderDropdownField = (field, options, displayNameField = null) => {
    const currentValue = selectedJob[field] || '';
    // Use isFieldEditable for dropdowns
    const editable = isFieldEditable(userRolesMapped, field, selectedJob);
    const isFieldDisabled = !editable;
    const fieldLabel = field === 'JobType' ? 'Station Type' : separateCamelCase(field);
    
    return (
      <Form.Group className="mb-3">
        <Form.Label>{fieldLabel}</Form.Label>
        <Form.Select
          id={field}
          name={field}
          value={currentValue}
          onChange={handleInputChange}
          disabled={isFieldDisabled}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option 
              key={typeof option === 'string' ? option : option.ID || option.displayName} 
              value={typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            >
              {typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            </option>
          ))}
          {!isValueInDropdown(currentValue, options) && currentValue && (
            <option value={currentValue} style={{ color: 'red' }}>
              *{currentValue}
            </option>
          )}
        </Form.Select>
      </Form.Group>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    // Don't try to format while the user is typing - just return the raw value
    // This prevents NaN from appearing during input
    return value;
  };

  // Function to check if value exists in dropdown options
  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  // Handle opening the engineer assignment modal
  const handleOpenEngineerModal = () => {
    setShowEngineerModal(true);
  };

  // Handle closing the engineer assignment modal
  const handleCloseEngineerModal = () => {
    setShowEngineerModal(false);
  };

  // Handle engineer selection from the modal
  const handleEngineerSelection = (engineerType, engineer, additionalInfo = {}) => {
    console.log('EditJobModal - handleEngineerSelection called with:', { engineerType, engineer: engineer.displayName, additionalInfo });
    
    setSelectedJob(prevJob => {
      const updatedJob = {
        ...prevJob,
        [engineerType]: engineer.displayName
      };
      
      // Update due date if provided
      if (additionalInfo.dueDateField && additionalInfo.dueDate) {
        updatedJob[additionalInfo.dueDateField] = additionalInfo.dueDate;
        console.log(`EditJobModal - Setting ${additionalInfo.dueDateField} to ${additionalInfo.dueDate}`);
      }
      
      // Update duration if provided
      if (additionalInfo.durationField && additionalInfo.duration) {
        updatedJob[additionalInfo.durationField] = additionalInfo.duration;
        console.log(`EditJobModal - Setting ${additionalInfo.durationField} to ${additionalInfo.duration}`);
      }
      
      console.log('EditJobModal - Updated job:', updatedJob);
      return updatedJob;
    });
    setShowEngineerModal(false);
    setChangesMade(true);
  };

  const handleCloseWithConfirmation = () => {
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      handleCloseModal();
    }
  };
  const handleConfirmClose = () => {
    setShowConfirmationModal(false);
    handleCloseModal();
  };

  // Handle ship date confirmation
  const handleConfirmShipDate = () => {
    setSelectedJob((prevJob) => ({ 
      ...prevJob, 
      ActualShipDate: pendingShipDate,
    JobStatus: 'Shipped',
      ProjectStatus: 'Shipped'
    }));
    setChangesMade(true);
    setShowShipConfirmationModal(false);
    setPendingShipDate('');
  };

  const handleCancelShipDate = () => {
    setShowShipConfirmationModal(false);
    setPendingShipDate('');
  };

  // Handle job cancellation confirmation
  const handleConfirmClearDates = () => {
    const dueDateFields = [
      'MechanicalDue', 'ElectricalDue', 'ControlsDue', 'EngineeringDue',
      'NPPipeFabDue', 'NPCSMDue', 'NPSuctionLineDue', 'NPInducerTubeDue',
      'NPBMXDue', 'NPFertigationDue', 'FabDue', 'SoftwareDue',
      'ControlPanelDue', 'HCPumpsDue', 'VTPumpsDue', 'AssemblyDue',
      'TestDue', 'PackDue', 'OriginalScheduledShipDate', 'CurrentPlanShipDate',
      'ScheduledArrivalDate'
    ];
    
    const engineerFields = [
      'MechanicalEngineer', 'ElectricalEngineer', 'ControlsEngineer', 'ProjectManager'
    ];
    
    setSelectedJob((prevJob) => {
      const updatedJob = { ...prevJob };
      
      // Clear all due date fields
      dueDateFields.forEach(field => {
        updatedJob[field] = null;
      });
      
      // Clear engineer fields
      engineerFields.forEach(field => {
        updatedJob[field] = null;
      });
      
      return updatedJob;
    });
    
    setChangesMade(true);
    setShowCancelConfirmationModal(false);
  };
  const handleCancelClearDates = () => {
    setShowCancelConfirmationModal(false);
  };

  // Function to handle alerts modal close
  const handleCloseAlertsModal = () => {
    setShowAlertsModal(false);
  };

  return (
    <>
      {/* Render SaveBanner at the top of the page, outside the modal */}
      {showSaveBanner && <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />}
      <Modal.Header closeButton onHide={handleCloseWithConfirmation}>
        <Modal.Title>Edit Job</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedJob && (
          <Form>            {/* Add TBD Notice at the top of the form */}
            {selectedJob.ID && <TbdNotice jobId={selectedJob.ID} />}            {/* Add Move Request Notice */}
            {selectedJob.ID && <MoveRequestNotice job={selectedJob} />}
            {/* Add Witness Test Notice */}
            {selectedJob && <WitnessTestNotice job={selectedJob} />}            {/* Add Long Build Notice */}
            {selectedJob && <LongBuildNotice job={selectedJob} pumpDetails={pumpDetails} />}
            {/* Add Change Order Notice */}
            {selectedJob.ID && <ChangeOrderNotice jobId={selectedJob.ID} job={selectedJob} />}
            
            <Row>
              <Col md={4}>{renderField('ID', true)}</Col>
              <Col md={4}>{renderDropdownField('JobStatus', options.filter(option => option.DropdownType === 'Job Status'))}</Col>
              <Col md={4}>{renderDropdownField('JobType', options.filter(option => option.DropdownType === 'Job Type'))}</Col>
            </Row>
            
            {/* Basic Job Information */}
            <h5 className="mt-4 mb-3 border-bottom pb-2">Basic Job Information</h5>
            <Row>
             <Col>{renderField('JobNumber', selectedJob.ScheduleOnly !== "Yes")}</Col>
                       <Col>{renderField('ProjectName', selectedJob.ScheduleOnly !== "Yes")}</Col>
                       <Col>{renderField('ModelNumber', selectedJob.ScheduleOnly !== "Yes")}</Col>
                      </Row>            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Order Date</Form.Label>
                  <Form.Control
                    type="date"
                    id="OrderDate"
                    name="OrderDate"
                    value={formatDateForForm(selectedJob.OrderDate) || ''}
                    onChange={handleInputChange}
                    readOnly={!isFieldEditable(userRolesMapped, 'OrderDate', selectedJob)}
                    disabled={!isFieldEditable(userRolesMapped, 'OrderDate', selectedJob)}
                  />
                </Form.Group>
              </Col>
              <Col>{renderField('ProductionRelease', false, true)}</Col>
              <Col>{renderDropdownField('ProjectManager', projectManagerOptions, 'displayName')}</Col>
            </Row><Row>
              <Col>{renderDropdownField('SalesRep', salesOptions, 'displayName')}</Col>
              <Col>{renderField('JobLocation')}</Col>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Dollar Value</Form.Label>
                  <Form.Control
                    type="text"
                    id="DollarValue"
                    name="DollarValue"
                    value={formatCurrency(selectedJob.DollarValue)}
                    onChange={handleInputChange}
                    readOnly={!isFieldEditable(userRolesMapped, 'DollarValue', selectedJob)}
                    disabled={!isFieldEditable(userRolesMapped, 'DollarValue', selectedJob)}
                  />
                </Form.Group>
              </Col>
              <Col>{renderField('PaymentTerms')}</Col>
            </Row>
            
            {/* Sections in Accordion */}
            <Accordion alwaysOpen>              {/* Engineering */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>Engineering</Accordion.Header>
                <Accordion.Body>                  <Row>
                    <Col>{renderDropdownField('MechanicalEngineer', mechOptions, 'displayName')}</Col>
                    <Col>{renderDropdownField('ElectricalEngineer', elecOptions, 'displayName')}</Col>
                    <Col>{renderDropdownField('ControlsEngineer', controlOptions, 'displayName')}</Col>
                  </Row>
                  <Row>
                    <Col md={2}>{renderField('MechanicalDue', false, true)}</Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          id="MechanicalDuration"
                          name="MechanicalDuration"
                          value={selectedJob.MechanicalDuration || '1'}
                          onChange={handleInputChange}
                          readOnly={!isFieldEditable(userRolesMapped, 'MechanicalDuration', selectedJob)}
                          disabled={!isFieldEditable(userRolesMapped, 'MechanicalDuration', selectedJob)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>{renderField('ElectricalDue', false, true)}</Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          id="ElectricalDuration"
                          name="ElectricalDuration"
                          value={selectedJob.ElectricalDuration || '1'}
                          onChange={handleInputChange}
                          readOnly={!isFieldEditable(userRolesMapped, 'ElectricalDuration', selectedJob)}
                          disabled={!isFieldEditable(userRolesMapped, 'ElectricalDuration', selectedJob)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>{renderField('ControlsDue', false, true)}</Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration (days)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          id="ControlsDuration"
                          name="ControlsDuration"
                          value={selectedJob.ControlsDuration || '1'}
                          onChange={handleInputChange}
                          readOnly={!isFieldEditable(userRolesMapped, 'ControlsDuration', selectedJob)}
                          disabled={!isFieldEditable(userRolesMapped, 'ControlsDuration', selectedJob)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col>{renderField('MechanicalDone', false, true)}</Col>
                    <Col>{renderField('ElectricalDone', false, true)}</Col>
                    <Col>{renderField('ControlsDone', false, true)}</Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Button 
                        variant="outline-primary" 
                        onClick={handleOpenEngineerModal}
                        className="w-100 mb-3"
                      >
                        <i className="bi bi-people-fill"></i> Find Available Engineers
                      </Button>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Fabrication */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>Fabrication</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('NPPipeFabDue', false, true)}</Col>
                    <Col>{renderField('NPPipeFabDone', false, true)}</Col>
                    <Col>{renderField('FusionBondRequired')}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('FabDue', false, true)}</Col>
                    <Col>{renderField('FabDone', false, true)}</Col>
                    <Col>{renderField('FabExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Control Panel */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>Control Panel</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('SoftwareDue', false, true)}</Col>
                    <Col>{renderField('SoftwareDone', false, true, true)}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('ControlPanelDue', false, true)}</Col>
                    <Col>{renderField('ControlPanelDone', false, true)}</Col>
                    <Col>{renderField('ControlPanelExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Pumps */}
              <Accordion.Item eventKey="3">
                <Accordion.Header>Pumps</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('HCPumpsDue', false, true)}</Col>
                    <Col>{renderField('HCPumpsDone', false, true)}</Col>
                    <Col>{renderField('VTPumpsDue', false, true)}</Col>
                    <Col>{renderField('PumpDone', false, true)}</Col>
                    <Col>{renderField('PumpExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Assembly */}
              <Accordion.Item eventKey="4">
                <Accordion.Header>Assembly</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('AssemblyDue', false, true)}</Col>
                    <Col>{renderField('AssemblyDone', false, true)}</Col>
                    <Col>{renderField('AssemblyExpectedCompletion', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Test */}
              <Accordion.Item eventKey="5">
                <Accordion.Header>Test</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('TestDue', false, true)}</Col>
                    <Col>{renderField('TestDone', false, true)}</Col>
                    <Col>{renderField('TestExpectedCompletion', false, true, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* NPCSMHDPE */}
              <Accordion.Item eventKey="6">
                <Accordion.Header>NP CSM HDPE</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('NPCSMDue', false, true)}</Col>
                    <Col>{renderField('NPCSMDone', false, true)}</Col>
                    <Col>{renderField('NPSuctionLineDue', false, true)}</Col>
                    <Col>{renderField('NPSuctionLineDone', false, true)}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('NPInducerTubeDue', false, true)}</Col>
                    <Col>{renderField('NPInducerTubeDone', false, true)}</Col>
                    <Col>{renderField('NPBMXDue', false, true)}</Col>
                    <Col>{renderField('NPBMXDone', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Shipping */}
              <Accordion.Item eventKey="7">
                <Accordion.Header>Shipping</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col>{renderField('PackDue', false, true)}</Col>
                    <Col>{renderField('TransportType')}</Col>
                  </Row>
                  <Row>
                    <Col>{renderField('OriginalScheduledShipDate', false, true)}</Col>
                    <Col>{renderField('CurrentPlanShipDate', false, true)}</Col>
                    <Col>{renderField('ActualShipDate', false, true)}</Col>
                    <Col>{renderField('ScheduledArrivalDate', false, true)}</Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Witness Test */}
              <Accordion.Item eventKey="8">
                <Accordion.Header>Witness Test</Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>{separateCamelCase('WitnessTestRequired')}</Form.Label>
                        <Form.Select
                          id="WitnessTestRequired"
                          name="WitnessTestRequired"
                          value={selectedJob.WitnessTestRequired || ''}
                          onChange={handleInputChange}
                          disabled={!isFieldEditable(userRolesMapped, 'WitnessTestRequired', selectedJob)}
                        >
                          <option value="">Select Option</option>
                          {options.filter(option => option.DropdownType === "Generic Yes/ No")
                            .map((option) => (
                              <option key={option.ID} value={option.DisplayText}>
                                {option.DisplayText}
                              </option>
                            ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {selectedJob.WitnessTestRequired === "Yes" && (
                      <>
                        <Col md={4}>
                          {renderField('WitnessTestDate', false, true)}
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{separateCamelCase('TestCriteria')}</Form.Label>
                            <Form.Select
                              id="TestCriteria"
                              name="TestCriteria"
                              value={selectedJob.TestCriteria || ''}
                              onChange={handleInputChange}
                              disabled={!isFieldEditable(userRolesMapped, 'TestCriteria', selectedJob)}
                            >
                              <option value="">Select Test Criteria</option>
                              {options.filter(option => option.DropdownType === "Test Criteria")
                                .map((option) => (
                                  <option key={option.ID} value={option.DisplayText}>
                                    {option.DisplayText}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </Row>
                  {selectedJob.WitnessTestRequired === "Yes" && (
                    <>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>{separateCamelCase('ComplianceCertificateRequired')}</Form.Label>
                            <Form.Select
                              id="ComplianceCertificateRequired"
                              name="ComplianceCertificateRequired"
                              value={selectedJob.ComplianceCertificateRequired || ''}
                              onChange={handleInputChange}
                              disabled={!isFieldEditable(userRolesMapped, 'ComplianceCertificateRequired', selectedJob)}
                            >
                              <option value="">Select Option</option>
                              {options.filter(option => option.DropdownType === "Generic Yes/ No")
                                .map((option) => (
                                  <option key={option.ID} value={option.DisplayText}>
                                    {option.DisplayText}
                                  </option>
                                ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={8}>
                          {/* Empty column for spacing */}
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="mb-3">
                            <Form.Label>{separateCamelCase('WitnessTestComments')}</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              id="WitnessTestComments"
                              name="WitnessTestComments"
                              value={selectedJob.WitnessTestComments || ''}
                              onChange={handleInputChange}
                              placeholder="Enter witness test comments"
                              readOnly={!isFieldEditable(userRolesMapped, 'WitnessTestComments', selectedJob)}
                              disabled={!isFieldEditable(userRolesMapped, 'WitnessTestComments', selectedJob)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
              
              {/* Move Request */}
              <Accordion.Item eventKey="9">
                <Accordion.Header>Move Request</Accordion.Header>
                <Accordion.Body>
                  {!selectedJob.MoveRequestDateInitiated ? (
                    <Row>
                      <Col className="text-center">
                        <Button 
                          variant="primary" 
                          disabled={!isFieldEditable(userRolesMapped, 'MoveRequestDateInitiated', selectedJob)}
                          onClick={async () => {
                            try {
                              // Create a new Date object for current date
                              const now = new Date();
                              
                              // Format the date as YYYY-MM-DD
                              const formattedDate = now.toISOString().split('T')[0];
                              
                              // Prepare the patch data
                              const moveRequestPayload = {
                                ID: selectedJob.ID,
                                MoveRequestPrevDate: selectedJob.CurrentPlanShipDate,
                                MoveRequestDateInitiated: formattedDate
                              };                              // Make the PATCH request
                              await dispatch(
                                patchDataThunk({
                                  url: `${apiUrls.generalProject}/?id=${selectedJob.ID}`,
                                  data: moveRequestPayload,
                                })
                              ).unwrap();
                              
                              // Update the local state with the new values
                              setSelectedJob({
                                ...selectedJob,
                                MoveRequestPrevDate: selectedJob.CurrentPlanShipDate,
                                MoveRequestDateInitiated: formattedDate
                              });
                              
                              // Show success notification
                              setSaveStatus("Move request initiated successfully");
                              setShowSaveBanner(true);
                              setTimeout(() => setShowSaveBanner(false), 3000);
                            } catch (error) {
                              console.error('Error initiating move request:', error);
                              setSaveStatus(`Failed to initiate move request: ${error.message}`);
                              setShowSaveBanner(true);
                              setTimeout(() => setShowSaveBanner(false), 5000);
                            }
                          }}
                        >
                          Initiate Move Request
                        </Button>
                      </Col>
                    </Row>
                  ) : (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Original Ship Date</Form.Label>
                            <Form.Control
                              type="date"
                              id="MoveRequestPrevDate"
                              name="MoveRequestPrevDate"
                              value={formatDateForForm(selectedJob.MoveRequestPrevDate) || ''}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date Initialized</Form.Label>
                            <Form.Control
                              type="date"
                              id="MoveRequestDateInitiated"
                              name="MoveRequestDateInitiated"
                              value={formatDateForForm(selectedJob.MoveRequestDateInitiated) || ''}
                              readOnly
                              disabled
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Requested Move Date</Form.Label>
                            <Form.Control
                              type="text"
                              id="MoveRequestRequestedDate"
                              name="MoveRequestRequestedDate"
                              value={selectedJob.MoveRequestRequestedDate || ''}
                              onChange={handleInputChange}
                              placeholder="Enter requested date"
                              readOnly={!isFieldEditable(userRolesMapped, 'MoveRequestRequestedDate', selectedJob)}
                              disabled={!isFieldEditable(userRolesMapped, 'MoveRequestRequestedDate', selectedJob)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Reason for Move</Form.Label>
                            <Form.Control
                              type="text"
                              id="MoveRequestReasonForMove"
                              name="MoveRequestReasonForMove"
                              value={selectedJob.MoveRequestReasonForMove || ''}
                              onChange={handleInputChange}
                              placeholder="Enter reason for move request"
                              readOnly={!isFieldEditable(userRolesMapped, 'MoveRequestReasonForMove', selectedJob)}
                              disabled={!isFieldEditable(userRolesMapped, 'MoveRequestReasonForMove', selectedJob)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Move Type
                              {getMoveRequestIcon({ ...selectedJob, MoveUpOut: selectedJob.MoveUpOut, MoveRequestDateMovedUpOut: selectedJob.MoveRequestDateMovedUpOut }) && (
                                <i 
                                  className={`${getMoveRequestIcon({ ...selectedJob, MoveUpOut: selectedJob.MoveUpOut, MoveRequestDateMovedUpOut: selectedJob.MoveRequestDateMovedUpOut })} ms-2`}
                                  style={{ 
                                    color: selectedJob.MoveRequestDateMovedUpOut ? 'green' : 'blue',
                                    fontSize: '1.1em'
                                  }}
                                ></i>
                              )}
                            </Form.Label>
                            <Form.Select
                              id="MoveUpOut"
                              name="MoveUpOut"
                              value={selectedJob.MoveUpOut || ''}
                              onChange={handleInputChange}
                              disabled={!isFieldEditable(userRolesMapped, 'MoveUpOut', selectedJob)}
                            >
                              <option value="">Select move type...</option>
                              <option value="Up">Move Up (earlier date)</option>
                              <option value="Out">Move Out (later date)</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Date Moved</Form.Label>
                            <Form.Control
                              type="date"
                              id="MoveRequestDateMovedUpOut"
                              name="MoveRequestDateMovedUpOut"
                              value={formatDateForForm(selectedJob.MoveRequestDateMovedUpOut) || ''}
                              onChange={handleInputChange}
                              readOnly={!isFieldEditable(userRolesMapped, 'MoveRequestDateMovedUpOut', selectedJob)}
                              disabled={!isFieldEditable(userRolesMapped, 'MoveRequestDateMovedUpOut', selectedJob)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            
            {/* Notes */}
            <h5 className="mt-4 mb-3 border-bottom pb-2">Notes</h5>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>{separateCamelCase('Notes')}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    id="Notes"
                    name="Notes"
                    value={selectedJob.Notes || ''}
                    onChange={handleInputChange}
                    placeholder="Enter notes about this job"
                    style={{ resize: "vertical" }}
                    readOnly={!isFieldEditable(userRolesMapped, 'Notes', selectedJob)}
                    disabled={!isFieldEditable(userRolesMapped, 'Notes', selectedJob)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="warning" 
          onClick={() => setShowQAFeedback(true)}
          className="me-auto"
        >
          <i className="bi bi-clipboard-check me-2"></i>
          Submit QA Feedback
        </Button>
        <Button variant="secondary" onClick={handleCloseWithConfirmation}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
      
      {/* Engineer Assignment Modal */}
      <EngineerAssignmentModal
        show={showEngineerModal}
        onHide={handleCloseEngineerModal}
        onSelectEngineer={handleEngineerSelection}
        engineeringDueDate={selectedJob?.EngineeringDue || ''}
        mechanicalDueDate={selectedJob?.MechanicalDue || ''}
        electricalDueDate={selectedJob?.ElectricalDue || ''}
        controlsDueDate={selectedJob?.ControlsDue || ''}
        mechanicalEngineers={mechOptions}
        electricalEngineers={elecOptions}
        controlsEngineers={controlOptions}
        selectedJob={selectedJob}
      />      {/* Confirmation Modal */}
      <Modal className="job-schedule-modal" show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Are you sure you want to close without saving?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClose}>
            Close Without Saving
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Ship Date Confirmation Modal */}
      <Modal className="job-schedule-modal" show={showShipConfirmationModal} onHide={handleCancelShipDate}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Job Shipment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are setting the actual ship date. This will mark both Job Status and Project Status as "Shipped".</p>
          <p><strong>Are you sure you want to mark this job as shipped?</strong></p>
          <div className="mt-3">
            <strong>Job Number:</strong> {selectedJob?.JobNumber}<br/>
            <strong>Project Name:</strong> {selectedJob?.ProjectName}<br/>
            <strong>Ship Date:</strong> {pendingShipDate}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelShipDate}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmShipDate}>
            Yes, Mark as Shipped
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Job Cancellation Confirmation Modal */}
      <Modal className="job-schedule-modal" show={showCancelConfirmationModal} onHide={handleCancelClearDates}>
        <Modal.Header closeButton>
          <Modal.Title>Clear Due Dates and Engineers</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You are setting the job status to "Canceled".</p>
          <p><strong>Would you like to clear all due dates and engineer assignments?</strong></p>
          <div className="mt-3">
            <strong>Job Number:</strong> {selectedJob?.JobNumber}<br/>
            <strong>Project Name:</strong> {selectedJob?.ProjectName}
          </div>
          <div className="mt-3 text-muted small">
            This will clear all due dates and engineer assignments for this job.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelClearDates}>
            No, Keep Current Data
          </Button>
          <Button variant="warning" onClick={handleConfirmClearDates}>
            Yes, Clear All Dates and Engineers
          </Button>        </Modal.Footer>
      </Modal>
      
      {/* Alerts Modal for non-admin users */}
      <AlertsModal
        show={showAlertsModal}
        onHide={handleCloseAlertsModal}
        job={selectedJob}
        pumpDetails={pumpDetails}
      />
      
      {/* QA Feedback Modal */}
      <QAFeedback
        show={showQAFeedback}
        onHide={() => setShowQAFeedback(false)}
        job={selectedJob}
        jobId={selectedJob?.ID}
      />
    </>
  );
};

EditJobModal.propTypes = {
  selectedJob: PropTypes.object,
  setSelectedJob: PropTypes.func.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  handleSaveChanges: PropTypes.func.isRequired,
};

export default EditJobModal;
